import { ReactNode, useLayoutEffect, useRef, useState } from "react";

import { SCREENS } from "@/lib/screens";

// A4 종이 폭(210mm ≈ 794px) — 폰에서는 이 폭으로 그린 뒤 화면 폭에 맞춰 통째로 줄인다.
export const A4_WIDTH_PX = 794;

const MD_QUERY = `(min-width: ${SCREENS.md.min})`;

// 폰·JS 켜짐·화면에서만 축소 전 문서를 숨기고, hydration 이 끝내 실패해도 2초 뒤 a4-reveal(globals.css)로 축소 안 된 종이를 드러낸다 — md 이상·인쇄·JS 꺼짐은 그대로 보인다.
export const A4_PENDING_HIDE =
  "[@media_screen_and_(max-width:640.98px)_and_(scripting:enabled)]:[&:not([data-a4-fit])]:invisible [@media_screen_and_(max-width:640.98px)_and_(scripting:enabled)]:[&:not([data-a4-fit])]:animate-[a4-reveal_0s_2s_forwards]";

// 쓸 수 있는 폭이 A4 보다 좁을 때만 줄인다(넓거나 아직 못 잰 0 이면 1).
export const a4FitScale = (available: number): number =>
  available > 0 && available < A4_WIDTH_PX ? available / A4_WIDTH_PX : 1;

export interface A4FitValue {
  scale: number;
  height: number;
}

// md 이상이면 null(손대지 않음), 폰이면 줄임 비율과 줄인 뒤 높이 — 높이를 맞춰야 아래에 빈 칸이 남지 않는다.
export const a4FitOf = (wide: boolean, available: number, docHeight: number): A4FitValue | null => {
  if (wide) return null;
  const scale = a4FitScale(available);
  return { scale, height: Math.ceil(docHeight * scale) };
};

export interface A4FitBox {
  readonly clientWidth: number;
  setAttribute(name: string, value: string): void;
}

export interface A4FitDoc {
  readonly offsetHeight: number;
  style: { width: string };
}

// 폰이면 a4: 가 먹는 상태(data-a4-fit · 794px)를 먼저 걸고 그 레이아웃에서 높이를 잰다 — 순서가 바뀌면 화면 폭 배치로 재서 아래가 잘린다. md 이상은 손대지 않는다(React 가 지운다).
export const measureA4Fit = (wide: boolean, box: A4FitBox, doc: A4FitDoc): A4FitValue | null => {
  if (wide) return null;
  const available = box.clientWidth;
  box.setAttribute("data-a4-fit", a4FitScale(available).toFixed(3));
  doc.style.width = `${A4_WIDTH_PX}px`;
  return a4FitOf(false, available, doc.offsetHeight);
};

// 같은 창 폭(innerWidth)에서는 잰 폭 중 가장 좁은 값을 쓴다 — 데스크톱 스크롤바가 생겼다 없어졌다 하며 비율이 오가는 것을 막는다(창 폭이 바뀌면 새로 재고, 못 잰 0 은 기억하지 않는다).
export const steadyBox = (target: A4FitBox, viewportWidth: () => number): A4FitBox => {
  let last: { viewport: number; width: number } | null = null;
  return {
    get clientWidth() {
      const viewport = viewportWidth();
      const width = target.clientWidth;
      if (width <= 0) return width;
      last = last && last.viewport === viewport ? { viewport, width: Math.min(last.width, width) } : { viewport, width };
      return last.width;
    },
    setAttribute: (name, value) => target.setAttribute(name, value),
  };
};

// 인쇄 미리보기 문서 감싸개 — 폰(md 미만)만 A4 폭을 줄여 보여 주고, md 이상·인쇄는 손대지 않는다(서버·첫 그림은 스타일 없음).
const A4Fit = ({ children }: { children: ReactNode }) => {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<A4FitValue | null>(null);

  useLayoutEffect(() => {
    const el = outer.current;
    const doc = inner.current;
    if (!el || !doc) return;
    const box = steadyBox(el, () => window.innerWidth);
    const media = window.matchMedia(MD_QUERY);
    const measure = () => {
      const next = measureA4Fit(media.matches, box, doc);
      setFit((prev) =>
        prev === next || (prev && next && prev.scale === next.scale && prev.height === next.height) ? prev : next
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observer.observe(doc);
    media.addEventListener("change", measure);
    return () => {
      observer.disconnect();
      media.removeEventListener("change", measure);
    };
  }, []);

  return (
    <div
      ref={outer}
      className={`print:h-auto! print:overflow-visible! ${A4_PENDING_HIDE}`}
      data-a4-fit={fit ? fit.scale.toFixed(3) : undefined}
      style={fit ? { height: fit.height, overflow: "hidden" } : undefined}>
      <div
        ref={inner}
        className="print:w-auto! print:transform-none!"
        style={fit ? { width: A4_WIDTH_PX, transform: `scale(${fit.scale})`, transformOrigin: "top left" } : undefined}>
        {children}
      </div>
    </div>
  );
};

export default A4Fit;
